import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { InboxItem, InboxItemStatus, InboxQuelle, InboxKlassifizierung } from '@/lib/database.types';
import { buildStoragePath } from '@/lib/inbox-items';

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

export function useInboxItems(opts?: {
  status?: InboxItemStatus | '';
  eingangs_quelle?: InboxQuelle | '';
  ki_klassifizierung?: InboxKlassifizierung | '';
}) {
  const [data, setData] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { status, eingangs_quelle, ki_klassifizierung } = opts || {};

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('inbox_items')
        .select('*')
        .order('eingangs_datum', { ascending: false })
        .limit(500);

      if (status) query = query.eq('status', status);
      if (eingangs_quelle) query = query.eq('eingangs_quelle', eingangs_quelle);
      if (ki_klassifizierung) query = query.eq('ki_klassifizierung', ki_klassifizierung);

      const { data: rows, error: err } = await query;
      if (err) {
        if (isTableNotFound(err)) {
          setData([]);
        } else {
          throw err;
        }
      } else {
        setData((rows as InboxItem[]) || []);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [status, eingangs_quelle, ki_klassifizierung]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export async function uploadInboxItem(file: File): Promise<InboxItem> {
  const storagePath = buildStoragePath(file.name);

  const { error: uploadErr } = await supabase.storage
    .from('inbox')
    .upload(storagePath, file, { upsert: false });

  if (uploadErr) throw uploadErr;

  // The 'inbox' bucket is private — getPublicUrl would produce non-functional URLs.
  // pdf_url stays null; callers use getSignedInboxUrl() to generate on-demand signed URLs.
  const insert = {
    status: 'neu' as const,
    eingangs_quelle: 'upload' as const,
    eingangs_datum: new Date().toISOString(),
    storage_path: storagePath,
    pdf_url: null,
    mime_type: file.type || null,
    dateigroesse_bytes: file.size,
    betreff: file.name,
  };

  const { data, error: insertErr } = await supabase
    .from('inbox_items')
    .insert(insert)
    .select()
    .single();

  if (insertErr) throw insertErr;
  return data as InboxItem;
}

export async function sortInboxItem(params: {
  id: string;
  zielmodul: string;
  zielId?: string | null;
  userId: string;
}): Promise<InboxItem> {
  const response = await fetch('/api/inbox', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'sort',
      id: params.id,
      zielmodul: params.zielmodul,
      ziel_id: params.zielId ?? null,
      user_id: params.userId,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || 'Sortierung fehlgeschlagen');
  }

  const json = await response.json() as { item: InboxItem };
  return json.item;
}

export async function ablehnenInboxItem(id: string): Promise<InboxItem> {
  const { data, error } = await supabase
    .from('inbox_items')
    .update({ status: 'abgelehnt' })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as InboxItem;
}

